'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBuilderStore } from '@/store/builderStore';
import Button from '@/app/components/ui/Button';
import {
  Sparkles,
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Check,
  AlertCircle,
  Wand2,
} from 'lucide-react';
import Image from 'next/image';
import { clsx } from 'clsx';

function ImageSkeleton() {
  return (
    <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-slate-100">
      <div className="absolute inset-0 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 animate-shimmer" />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <div className="w-12 h-12 rounded-full bg-slate-200 animate-pulse" />
        <div className="w-24 h-3 bg-slate-200 rounded animate-pulse" />
        <div className="w-16 h-2 bg-slate-200 rounded animate-pulse" />
      </div>
    </div>
  );
}

export default function Preview() {
  const {
    product,
    material,
    dimensions,
    customer,
    generatedImages,
    selectedImageIndex,
    isGenerating,
    setGeneratedImages,
    setSelectedImageIndex,
    setIsGenerating,
    setCurrentStep,
  } = useBuilderStore();

  const [error, setError] = useState<string | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(selectedImageIndex);

  const generateImages = async () => {
    setError(null);
    setIsGenerating(true);
    try {
      const response = await fetch('/api/generate-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productName: product.productName,
          description: product.description,
          material: material.material,
          finish: material.finish,
          color: material.color,
          height: dimensions.height,
          width: dimensions.width,
          depth: dimensions.depth,
          weight: dimensions.weight,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate images');
      }

      setGeneratedImages(data.images);
      setSelectedImageIndex(0);
      setCarouselIndex(0);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to generate images. Please try again.'
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectImage = (index: number) => {
    setSelectedImageIndex(index);
    setCarouselIndex(index);
  };

  const nextImage = () => {
    const newIdx = (carouselIndex + 1) % generatedImages.length;
    setCarouselIndex(newIdx);
  };

  const prevImage = () => {
    const newIdx = (carouselIndex - 1 + generatedImages.length) % generatedImages.length;
    setCarouselIndex(newIdx);
  };

  const hasImages = generatedImages.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
            <Sparkles size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-800 tracking-tight">AI Generated Preview</h2>
            <p className="text-sm text-slate-400">
              Generate 4 photorealistic product images using AI
            </p>
          </div>
        </div>

        {/* Product summary */}
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Generating for:
          </p>
          <div className="flex flex-wrap gap-2">
            {product.productName && (
              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-lg text-xs font-semibold">
                📦 {product.productName}
              </span>
            )}
            {material.material && (
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold">
                🔩 {material.material}
              </span>
            )}
            {material.finish && (
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-semibold">
                ✨ {material.finish}
              </span>
            )}
            {(dimensions.height || dimensions.width) && (
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-semibold">
                📐{' '}
                {[dimensions.height, dimensions.width, dimensions.depth]
                  .filter(Boolean)
                  .join(' × ')}
                {' cm'}
              </span>
            )}
          </div>
        </div>

        {/* Generate button */}
        <div className="flex flex-col items-center gap-4">
          <Button
            onClick={generateImages}
            loading={isGenerating}
            size="lg"
            className="gap-3 min-w-64 shadow-xl shadow-amber-500/25"
            disabled={isGenerating}
          >
            <Wand2 size={20} />
            {isGenerating
              ? 'Generating AI Images...'
              : hasImages
              ? 'Regenerate Images'
              : 'Generate AI Preview'}
          </Button>
          {isGenerating && (
            <p className="text-xs text-slate-400 animate-pulse">
              AI is crafting your product images... This may take 30-60 seconds
            </p>
          )}
        </div>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-3 p-4 bg-red-50 border border-red-200 rounded-xl"
          >
            <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-700">Generation Failed</p>
              <p className="text-xs text-red-600 mt-1">{error}</p>
            </div>
          </motion.div>
        )}

        {/* Loading skeletons */}
        {isGenerating && (
          <div className="grid grid-cols-2 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <ImageSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Generated images */}
        {hasImages && !isGenerating && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-4"
          >
            {/* Carousel */}
            <div className="relative">
              <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-slate-100 shadow-xl">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={carouselIndex}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="absolute inset-0"
                  >
                    <Image
                      src={generatedImages[carouselIndex]}
                      alt={`Generated product ${carouselIndex + 1}`}
                      fill
                      className="object-contain"
                      unoptimized
                    />
                  </motion.div>
                </AnimatePresence>

                {/* Selected badge */}
                {carouselIndex === selectedImageIndex && (
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                    <Check size={12} strokeWidth={3} />
                    Selected
                  </div>
                )}

                {/* Image counter */}
                <div className="absolute top-3 left-3 bg-black/60 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  {carouselIndex + 1} / {generatedImages.length}
                </div>
              </div>

              {/* Navigation arrows */}
              {generatedImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 border border-slate-100"
                  >
                    <ChevronLeft size={20} className="text-slate-700" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 border border-slate-100"
                  >
                    <ChevronRight size={20} className="text-slate-700" />
                  </button>
                </>
              )}
            </div>

            {/* Dot indicators */}
            <div className="flex justify-center gap-2">
              {generatedImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCarouselIndex(idx)}
                  className={clsx(
                    'w-2.5 h-2.5 rounded-full transition-all duration-200',
                    carouselIndex === idx
                      ? 'bg-amber-500 scale-125'
                      : 'bg-slate-300 hover:bg-slate-400'
                  )}
                />
              ))}
            </div>

            {/* Thumbnail grid */}
            <div className="grid grid-cols-4 gap-3">
              {generatedImages.map((img, idx) => (
                <motion.button
                  key={idx}
                  onClick={() => handleSelectImage(idx)}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className={clsx(
                    'relative aspect-square rounded-xl overflow-hidden border-2 transition-all duration-200 shadow-sm',
                    {
                      'border-amber-500 shadow-amber-200 shadow-md': selectedImageIndex === idx,
                      'border-slate-200 hover:border-amber-300': selectedImageIndex !== idx,
                    }
                  )}
                >
                  <Image
                    src={img}
                    alt={`Option ${idx + 1}`}
                    fill
                    className="object-cover"
                    unoptimized
                  />
                  {selectedImageIndex === idx && (
                    <div className="absolute inset-0 bg-amber-500/10 flex items-center justify-center">
                      <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center shadow-lg">
                        <Check size={12} strokeWidth={3} className="text-white" />
                      </div>
                    </div>
                  )}
                  {/* View in carousel */}
                  {carouselIndex === idx && (
                    <div className="absolute bottom-1 left-1 w-3 h-3 rounded-full bg-blue-500 border border-white" />
                  )}
                </motion.button>
              ))}
            </div>

            <p className="text-xs text-slate-400 text-center">
              Click a thumbnail to select it as your preferred image, or use arrows to browse
            </p>
          </motion.div>
        )}

        {/* No images yet */}
        {!hasImages && !isGenerating && !error && (
          <div className="text-center py-12 text-slate-400">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Wand2 size={36} className="text-slate-300" />
            </div>
            <p className="font-semibold text-slate-500 mb-1">Ready to generate</p>
            <p className="text-sm">Click the button above to create your AI product preview</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between pt-2">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={() => setCurrentStep(3)}
            className="gap-2"
          >
            <ArrowLeft size={18} />
            Back
          </Button>
          <Button
            type="button"
            size="lg"
            onClick={() => setCurrentStep(5)}
            disabled={!hasImages}
            className="gap-2 min-w-40"
          >
            Review & Submit
            <ArrowRight size={18} />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
